import { Fragment } from 'react'

import { EXTERNAL_LINKS } from '@/constants/external-link'
import { useTheme } from '@/context'
import { useIsMd } from '@/hooks'
import { CommonDark, CommonDiscord, CommonLight, CommonTwitter } from '@/icons'
import { cn } from '@/lib'

import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { Button } from '../ui/button'
import { Text } from '../ui/text'

type LinkItem = {
  href?: string
  text: string
  local?: boolean
  items?: LinkItem[]
  requireAuth?: boolean
}

const socials = [
  {
    href: EXTERNAL_LINKS.X,
    icon: <CommonTwitter size={12} />,
  },
  {
    href: EXTERNAL_LINKS.DISCORD,
    icon: <CommonDiscord size={16} />,
  },
]

function MenuLink({
  item,
  pathname,
  itemClick,
  isMd,
}: {
  item: LinkItem
  pathname: string
  itemClick?: () => void
  isMd: boolean
}) {
  const isActive = pathname === item.href
  return (
    <Link
      onClick={(e) => {
        e.stopPropagation()
        itemClick?.()
      }}
      target='_blank'
      to={item.href ?? ''}
      key={item.href}
      className={cn('duration-300 transition-all group', {})}
    >
      <Text
        size={isMd ? 14 : 16}
        as='span'
        color='white-48'
        className={cn('group-hover:text-main-100 duration-300', {
          'text-main-100': isActive,
        })}
      >
        {item.text}
      </Text>
    </Link>
  )
}
const APPEARANCE_OPTIONS = [
  {
    value: 'system',
    icon: 'Auto',
  },
  {
    value: 'light',
    icon: <CommonLight className='size-5' />,
  },
  {
    value: 'dark',
    icon: <CommonDark className='size-5' />,
  },
] as const

interface AppearanceProps {
  onChange: (value: string) => void
}
const Appearance = ({ onChange }: AppearanceProps) => {
  const { t } = useTranslation()
  const { activeTheme, setTheme } = useTheme()
  return (
    <div>
      <Text size={16}>{t('title.THEME')}</Text>
      <div className='flex gap-1 p-[1px] mt-1'>
        {APPEARANCE_OPTIONS.map((option) => (
          <Button
            key={option.value}
            size='sm'
            variant={option.value === activeTheme ? 'default' : 'secondary'}
            onClick={() => {
              setTheme(option.value)
              onChange(option.value)
            }}
          >
            {option.icon}
          </Button>
        ))}
      </div>
    </div>
  )
}
interface MenuProps {
  setOpenMenu: (value: string) => void
}
export const Menu = ({ setOpenMenu }: MenuProps) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const links: LinkItem[] = [
    {
      text: t('menu.DEVELOPERS'),
      items: [
        { href: 'https://docs.optimex.xyz', text: t('menu.DOCS') },
        { href: 'https://github.com/optimex-xyz', text: t('menu.GITHUB') },
      ],
    },
    {
      text: t('menu.TERMS_POLICY'),
      items: [
        { href: 'https://optimex.com/files/optimex_tou.pdf', text: t('menu.TERMS_OF_USE') },
        { href: 'https://optimex.com/files/optimex_privacy_policy.pdf', text: t('menu.PRIVACY_POLICY') },
        { href: 'https://optimex.com/files/optimex_disclaimers.pdf', text: t('menu.DISCLAIMERS') },
        { href: 'https://docs.optimex.xyz/how-to-swap/connect-your-wallet', text: t('menu.USER_GUIDE') },
      ],
    },
  ]

  const isMd = useIsMd()
  return (
    <div className='md:w-[180px] w-full flex flex-col gap-6'>
      <div className={`overflow-y-auto gap-4 flex flex-col flex-grow w-full`}>
        <Appearance onChange={() => setOpenMenu('')} />
        {links.map((link) => (
          <Fragment key={link.text}>
            <div className='flex flex-col gap-2'>
              <Text as='span' className='duration-300 transition-all cursor-default' size={16}>
                {link.text}
              </Text>
              {link.items?.map((item) => <MenuLink key={item.href} item={item} pathname={pathname} isMd={isMd} />)}
            </div>
          </Fragment>
        ))}
      </div>
      <div className='flex gap-2'>
        {socials.map((link) => (
          <Link key={link.href} target='_blank' to={link.href}>
            <button className='rounded-full text-size-16 text-white-48  duration-300 transition-all hover:scale-[1.1] hover:text-main-100 size-6 border border-secondary hover:border-orange-100 p-1 px-0 flex items-center justify-center'>
              {link.icon}
            </button>
          </Link>
        ))}
      </div>
    </div>
  )
}
